import { useMemo } from 'react';

import { BreakdownsResult } from 'api/useAnalytics';
import { EStatisticType } from 'views/Statistics/StatisticsView.types';

import { CHART_COLORS, convertToPercentage, formatValue } from '../../utils';
import { PieChartConfig, TransformedPieData } from '../pie-chart.types';

type UsePieChartConfigParams = {
  breakdown: BreakdownsResult | undefined;
  shouldDisplayValue?: boolean;
  shouldDisplayLabel?: boolean;
};

export function usePieChartConfig({
  breakdown,
  shouldDisplayValue = false,
  shouldDisplayLabel = true
}: UsePieChartConfigParams): TransformedPieData {
  return useMemo(() => {
    if (!breakdown) {
      return { chartConfig: {}, chartData: [] };
    }

    const isPercentage = breakdown.type === EStatisticType.PERCENTAGE;

    const chartConfig = breakdown.values.reduce(
      (acc, { breakdownValue, value }, index) => {
        const numericValue = parseFloat(value.toString());
        const convertedValue = isPercentage ? convertToPercentage(numericValue) : numericValue;
        const displayValue = String(formatValue(convertedValue, breakdown.type));

        acc[breakdownValue as string] = {
          label: shouldDisplayValue
            ? displayValue
            : shouldDisplayLabel
              ? (breakdownValue as string)
              : '',
          color: CHART_COLORS[index % CHART_COLORS.length]
        };
        return acc;
      },
      {} as PieChartConfig
    );

    const chartData = breakdown.values.map((item, index) => {
      const rawValue = parseFloat(item.value.toString());
      const value = isPercentage ? convertToPercentage(rawValue) : rawValue;
      
      return {
        breakdownValue: item.breakdownValue as string,
        value,
        fill: CHART_COLORS[index % CHART_COLORS.length]
      };
    });

    return { chartConfig, chartData };
  }, [breakdown, shouldDisplayValue, shouldDisplayLabel]);
}
